import * as Slider from '@radix-ui/react-slider'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { DropdownOption, KVInputProfile } from 'types'

const fadeInModal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-0.5rem);
  }
  to {
    opacity: 0.95;
    transform: scale(1) translateY(0);
  }
`

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContainer = styled.div`
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  color: white;
  line-height: 1.25rem;
  text-align: center;
  width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  opacity: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${fadeInModal} 0.3s ease-out forwards;
`

const SettingsTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  letter-spacing: 0.025rem;
  opacity: 0.95;
`

const ControlGroup = styled.div`
  margin-bottom: 0.75rem;
  text-align: left;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.25rem;
  font-weight: 500;
`

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StyledSliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
  flex: 1;
`

const StyledSliderTrack = styled(Slider.Track)`
  background-color: rgba(255, 255, 255, 0.15);
  position: relative;
  border-radius: 9999px;
  height: 4px;
  flex: 1;
`

const StyledSliderRange = styled(Slider.Range)`
  position: absolute;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  height: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  display: block;
  width: 12px;
  height: 20px;
  background-color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.95);
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`

const SliderValue = styled.div`
  min-width: 2.5rem;
  font-size: 0.875rem;
  text-align: right;
  color: rgba(255, 255, 255, 0.9);
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const UpdateButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(1px);
  }
`

const StyledDropdownContainer = styled.div`
  .rs-picker-default .rs-picker-toggle.rs-btn {
    background-color: rgba(255, 255, 255, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    border-radius: 0.25rem !important;
  }

  .rs-picker-toggle-value {
    color: white !important;
    font-weight: 500 !important;
  }

  .rs-picker-toggle-placeholder {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .rs-btn:hover {
    background-color: rgba(255, 255, 255, 0.25) !important;
    border-color: rgba(255, 255, 255, 0.4) !important;
  }

  .rs-btn:focus {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3) !important;
  }
`

const inputProfileOptions: DropdownOption[] = [
  { value: KVInputProfile.Default, label: 'Default' },
  { value: KVInputProfile.Switched, label: 'Switched' },
  { value: KVInputProfile.Creo, label: 'Creo' },
]

export const Settings: React.FC = () => {
  const { sendCommand, settingsOpen, setSettingsOpen, settings } =
    useKestrelViewerContext()

  // Local state for settings - initialized with context values
  const [orbitSpeed, setOrbitSpeed] = useState(settings?.orbitSpeed ?? 0.7)
  const [inputProfile, setInputProfile] = useState<KVInputProfile>(
    settings?.inputProfile ?? KVInputProfile.Default
  )

  // Update local state when settings from context change
  useEffect(() => {
    if (settings) {
      setOrbitSpeed(settings.orbitSpeed)
      setInputProfile(settings.inputProfile)
    }
  }, [settings])

  if (!settingsOpen) return null

  const handleClose = () => {
    setSettingsOpen(false)
  }

  const handleUpdate = () => {
    // Send commands to update the Kestrel viewer settings
    // Kestrel viewer will update the settings in the backend and send a settingsUpdated message to the client
    // The client will update the state in KV Context, which should be reflected in the hook above
    sendCommand({
      command: 'updateSettings',
      settings: {
        orbitSpeed: orbitSpeed,
        inputProfile: inputProfile.toString(),
      },
    })

    // Close the settings popup after updating
    handleClose()
  }

  return (
    <ModalBackdrop onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <SettingsTitle>Settings</SettingsTitle>
        <ControlGroup>
          <ControlLabel>Orbit Speed</ControlLabel>
          <SliderContainer>
            <StyledSliderRoot
              min={0.1}
              max={2.0}
              step={0.1}
              value={[orbitSpeed]}
              onValueChange={(value) => setOrbitSpeed(value[0])}>
              <StyledSliderTrack>
                <StyledSliderRange />
              </StyledSliderTrack>
              <StyledSliderThumb />
            </StyledSliderRoot>
            <SliderValue>{orbitSpeed.toFixed(1)}</SliderValue>
          </SliderContainer>
        </ControlGroup>

        <ControlGroup>
          <ControlLabel>Input Profile</ControlLabel>
          <StyledDropdownContainer>
            <Dropdown
              options={inputProfileOptions}
              onValueChange={(value: string) =>
                setInputProfile(value as KVInputProfile)
              }
              defaultValue={inputProfile}
              placeholder='Select input profile'
              $backgroundColor='rgba(255, 255, 255, 0.1)'
              $borderColor='rgba(255, 255, 255, 0.2)'
              searchable={false}
              cleanable={false}
            />
          </StyledDropdownContainer>
        </ControlGroup>

        <ButtonContainer>
          <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
        </ButtonContainer>
      </ModalContainer>
    </ModalBackdrop>
  )
}
